import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * BackNavigationContext
 *
 * Responsibilities:
 * - Maintain an in-app navigation stack (for predictable back behavior)
 * - Provide goBack() which prefers the app stack, then per-page fallback, then a global default
 * - Allow pages to mark themselves as root, provide a fallback, or be excluded from the stack
 *
 * Notes:
 * - This file exports a default BackNavigationProvider and a named hook useBackNavigation().
 * - Keep provider inside Router (it uses useLocation/useNavigate). Wrap app pages with the provider.
 */

const BackNavigationContext = createContext(null);

export const useBackNavigation = () => {
  const ctx = useContext(BackNavigationContext);
  if (!ctx) {
    throw new Error(
      "useBackNavigation must be used within BackNavigationProvider"
    );
  }
  return ctx;
};

const BackNavigationProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [stack, setStack] = useState([]);
  const stackRef = useRef(stack);
  useEffect(() => {
    stackRef.current = stack;
  }, [stack]);

  const prevLocationRef = useRef(location);
  const initialRef = useRef(true);

  // metadata for the currently mounted page (reset on location change)
  const [currentMeta, setCurrentMeta] = useState({
    isRoot: false,
    fallback: null,
    exclude: false,
  });

  useEffect(() => {
    // skip on initial mount (we don't want to push an entry before user navigates)
    if (initialRef.current) {
      initialRef.current = false;
      prevLocationRef.current = location;
      return;
    }

    const prev = prevLocationRef.current;

    // If the previous page isn't excluded, push it onto our stack unless it's the same route
    if (!currentMeta.exclude) {
      if (
        prev &&
        !(
          prev.pathname === location.pathname && prev.search === location.search
        )
      ) {
        setStack((s) => [
          ...s,
          { pathname: prev.pathname, search: prev.search },
        ]);
      }
    }

    // Reset page-scoped metadata for the newly mounted page
    setCurrentMeta({ isRoot: false, fallback: null, exclude: false });
    prevLocationRef.current = location;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const canGoBack =
    !currentMeta.isRoot && (stack.length > 0 || Boolean(currentMeta.fallback));

  const goBack = () => {
    // Prefer explicit page fallback if provided
    if (currentMeta.fallback) {
      navigate(currentMeta.fallback);
      return;
    }

    const s = stackRef.current;
    if (s && s.length > 0) {
      const last = s[s.length - 1];
      // remove last entry and navigate there
      setStack((st) => st.slice(0, -1));
      navigate(last.pathname + (last.search || ""));
      return;
    }

    navigate("/projects");
  };

  // Page-level API
  const markAsRoot = (isRoot = true) =>
    setCurrentMeta((m) => ({ ...m, isRoot }));
  const setPageFallback = (path) =>
    setCurrentMeta((m) => ({ ...m, fallback: path }));
  const excludeFromHistory = (flag = true) =>
    setCurrentMeta((m) => ({ ...m, exclude: flag }));

  const clearStack = () => setStack([]);

  return (
    <BackNavigationContext.Provider
      value={{
        canGoBack,
        goBack,
        markAsRoot,
        setPageFallback,
        excludeFromHistory,
        clearStack,
        // exposed for debugging/testing if desired
        stack,
      }}
    >
      {children}
    </BackNavigationContext.Provider>
  );
};

export default BackNavigationProvider;
